import { useState } from "react";
import {
  Box,
  CloseButton,
  Flex,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Download,
  FolderDown,
  FolderX,
  X,
} from "lucide-react";
import { AlertTriangle } from "lucide-react";
import type { DownloadWarning } from "../contexts/DownloadManagerContext";
import type { DownloadJob } from "../contexts/DownloadManagerContext";
import { useDownloadManager } from "../contexts/DownloadManagerContext";

const JobRow = ({
  job,
  onDismiss,
  onCancel,
}: {
  job: DownloadJob;
  onDismiss: () => void;
  onCancel: () => void;
}) => {
  const rowBorder = useColorModeValue("gray.100", "whiteAlpha.100");
  const labelColor = useColorModeValue("gray.700", "whiteAlpha.900");
  const subColor = useColorModeValue("gray.500", "whiteAlpha.600");

  return (
    <HStack
      spacing={3}
      px={4}
      py={3}
      align="center"
      borderTopWidth="1px"
      borderColor={rowBorder}
    >
      <Box flexShrink={0}>
        {job.status === "pending" && <Spinner size="sm" color="ggBlue.500" />}
        {job.status === "done"    && <Icon as={Download}    boxSize={5} color="green.400"  />}
        {job.status === "error"   && <Icon as={AlertCircle} boxSize={5} color="red.400"    />}
        {job.status === "empty"   && <Icon as={FolderX}     boxSize={5} color="orange.400" />}
      </Box>

      <Box flex={1} minW={0}>
        <Text fontSize="sm" fontWeight="semibold" color={labelColor} noOfLines={1}>
          {job.label}
        </Text>
        <Text fontSize="xs" color={subColor} mt={0.5}>
          {job.status === "pending" && "Estamos preparando seus arquivos..."}
          {job.status === "done"    && "Pronto — clique para baixar"}
          {job.status === "error"   && "Erro ao gerar o arquivo"}
          {job.status === "empty"   && (job.warnings?.length
            ? `${job.warnings.length} candidato(s) sem dados de admissão`
            : "Nenhum documento encontrado para este filtro")}
        </Text>
      </Box>

      <HStack spacing={1} flexShrink={0}>
        {job.warnings && job.warnings.length > 0 && (
          <Tooltip
            hasArrow
            placement="top"
            maxW="320px"
            gutter={8}
            zIndex={9999999}
            label={
              <Box>
                <Text fontWeight="bold" fontSize="xs" mb={1}>
                  Candidatos sem dados de admissão completos:
                </Text>
                {job.warnings.map((w: DownloadWarning, i: number) => (
                  <Box key={i} mb={1}>
                    <Text fontSize="xs" fontWeight="semibold">{w.name}</Text>
                    <Text fontSize="2xs" color="whiteAlpha.700">
                      Faltam: {w.missing_fields.join(", ")}
                    </Text>
                  </Box>
                ))}
              </Box>
            }
          >
            <IconButton
              aria-label="Ver observações"
              icon={<Icon as={AlertTriangle} boxSize={4} />}
              size="sm"
              variant="ghost"
              colorScheme="orange"
            />
          </Tooltip>
        )}
        {job.status === "done" && job.signedUrl && (
          <Tooltip label="Baixar arquivo" placement="top" hasArrow>
            <IconButton
              as="a"
              href={job.signedUrl}
              download={job.filename ?? "documentos.zip"}
              aria-label="Baixar"
              icon={<Icon as={Download} boxSize={4} />}
              size="sm"
              variant="ghost"
              colorScheme="ggBlue"
            />
          </Tooltip>
        )}
        {job.status === "pending" && (
          <Tooltip label="Cancelar" placement="top" hasArrow>
            <IconButton
              aria-label="Cancelar download"
              icon={<Icon as={X} boxSize={4} />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={onCancel}
            />
          </Tooltip>
        )}
        {(job.status === "done" || job.status === "error" || job.status === "empty") && (
          <CloseButton size="md" onClick={onDismiss} />
        )}
      </HStack>
    </HStack>
  );
};

export const DownloadManagerPanel = () => {
  const { jobs, dismissJob, dismissAll } = useDownloadManager();
  const [minimized, setMinimized] = useState(false);

  const bg          = useColorModeValue("white", "#1a2744");
  const headerBg    = useColorModeValue("gray.50", "#0f2040");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.200");
  const headerText  = useColorModeValue("gray.700", "whiteAlpha.900");

  if (jobs.length === 0) return null;

  const pendingCount = jobs.filter((j) => j.status === "pending").length;

  return (
    <Box
      position="fixed"
      bottom={5}
      right={5}
      zIndex={1000}
      w="440px"
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="2xl"
      bg={bg}
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        px={4}
        py={3}
        bg={headerBg}
        align="center"
        justify="space-between"
        cursor="pointer"
        userSelect="none"
        onClick={() => setMinimized((v) => !v)}
      >
        <HStack spacing={2}>
          <Icon as={FolderDown} boxSize={4} color="ggBlue.500" />
          <Text fontSize="sm" fontWeight="semibold" color={headerText}>
            {pendingCount > 0
              ? `Preparando ${pendingCount} download${pendingCount > 1 ? "s" : ""}...`
              : "Downloads"}
          </Text>
        </HStack>
        <HStack spacing={1}>
          <IconButton
            aria-label={minimized ? "Expandir" : "Minimizar"}
            icon={<Icon as={minimized ? ChevronUp : ChevronDown} boxSize={4} />}
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setMinimized((v) => !v); }}
          />
          {pendingCount === 0 && (
            <CloseButton
              size="md"
              onClick={(e) => { e.stopPropagation(); dismissAll(); }}
            />
          )}
        </HStack>
      </Flex>

      {/* Job list */}
      {!minimized && (
        <VStack spacing={0} align="stretch" maxH="280px" overflowY="auto">
          {jobs.map((job) => (
            <JobRow
              key={job.id}
              job={job}
              onDismiss={() => dismissJob(job.id)}
              onCancel={() => dismissJob(job.id)}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
};
